import { type ExternalToast, toast as sonnerToast } from 'sonner';

/**
 * Routes that should display toasts on the right side
 */
const RIGHT_SIDE_TOAST_ROUTES = [
  '/wireframes',
  '/editor',
  // All project pages
];

/**
 * Determines toast position based on current route
 */
const getPosition = (): 'bottom-left' | 'bottom-right' => {
  if (typeof window === 'undefined') return 'bottom-left';

  const pathname = window.location.pathname;
  const shouldUseRightSide = RIGHT_SIDE_TOAST_ROUTES.some((route) =>
    pathname.includes(route)
  );

  return shouldUseRightSide ? 'bottom-right' : 'bottom-left';
};

/**
 * Smart toast wrapper that automatically positions based on current route.
 * - Project pages (editor, wireframes, etc.): bottom-right
 * - Other pages: bottom-left
 *
 * @example
 * ```tsx
 * import { toast } from '@/hooks/useToast';
 *
 * toast.success('Success message');
 * toast.error('Error message');
 * ```
 */
export const toast = {
  success: (message: string, options?: ExternalToast) =>
    sonnerToast.success(message, { position: getPosition(), ...options }),

  error: (message: string, options?: ExternalToast) =>
    sonnerToast.error(message, { position: getPosition(), ...options }),

  info: (message: string, options?: ExternalToast) =>
    sonnerToast.info(message, { position: getPosition(), ...options }),

  warning: (message: string, options?: ExternalToast) =>
    sonnerToast.warning(message, { position: getPosition(), ...options }),

  loading: (message: string, options?: ExternalToast) =>
    sonnerToast.loading(message, { position: getPosition(), ...options }),

  promise: sonnerToast.promise,
  dismiss: sonnerToast.dismiss,

  message: (message: string, options?: ExternalToast) =>
    sonnerToast(message, { position: getPosition(), ...options }),
};
